<script setup>
import { formApi } from '@/configs/forms-api';
import { ref, computed } from 'vue';
import { useField, useForm } from 'vee-validate';
import { useI18n } from 'vue-i18n';
import { useRouter, useRoute } from '#app';
import { createConsultationSchema } from '@/configs/validation-schemas';
import { useRequest } from '@/composables/useRequest';
import { useThanksData } from '@/composables/useThanksData';
import { loadRecaptchaScript } from '@/composables/useRecaptcha';
import { useGlobalStore } from '@/stores/global';
defineProps({
  view: {
    type: String,
    default: () => '',
  },
});
const { t } = useI18n();
const thanksData = useThanksData();
const globalStore = useGlobalStore();
const router = useRouter();
const route = useRoute();

const consultationSchema = computed(() => createConsultationSchema(t));
const loading = ref(false);

const captchaSiteKey = computed(() => globalStore.captchaSiteKey);

const { handleSubmit, resetForm, setErrors } = useForm({
  validationSchema: consultationSchema,
  initialValues: { phone: '' },
});

const phone = useField('phone');

const phoneMaskRegExp = /^\+380 \d{2} \d{3} \d{4}$/;
const isRequiredFilled = computed(() => {
  if (!phoneMaskRegExp.test(phone.value.value || '')) return false;

  return consultationSchema.value.isValidSync({ phone: phone.value.value });
});

const getCaptchaToken = async () => {
  const loaded = await loadRecaptchaScript(captchaSiteKey.value);

  return new Promise((resolve) => {
    if (!loaded || !window.grecaptcha || !captchaSiteKey.value) {
      console.warn('ReCaptcha script not loaded or site key missing');
      resolve(null);
      return;
    }

    window.grecaptcha.ready(() => {
      window.grecaptcha
        .execute(captchaSiteKey.value, { action: 'footer_feedback' })
        .then((token) => resolve(token))
        .catch((err) => {
          console.error('ReCaptcha execution error:', err);
          resolve(null);
        });
    });
  });
};
const onSubmit = handleSubmit(async (values) => {
  loading.value = true;
  const currentFullUrl = process.client ? window.location.href : '';
  try {
    const token = await getCaptchaToken();
    const res = await useRequest(`/forms/${formApi['footer_feedback']}`, {
      method: 'POST',
      body: {
        phone: values.phone,
        page: currentFullUrl,
        token: token ? token : {},
      },
    });

    if (res && res.success !== false) {
      if (process.client) {
        window.dataLayer = window.dataLayer || [];
        const translatedFormName = t(`application_popup.footer_feedback_title`);
        window.dataLayer.push({
          event: 'form_submit',
          form_name: translatedFormName,
          form_id: 'footer_feedback',
          form_location: currentFullUrl,
          form_status: 'success',
        });
      }
      resetForm();
      thanksData.value = res;

      const langPrefix = route.params.lang ? `/${route.params.lang}` : '';
      router.push(`${langPrefix}/thanks`);
    } else if (res && res.errors) {
      setErrors(res.errors);
      console.warn(res.header || 'Validation Error');
    }
  } catch (e) {
    if (e.data && e.data.errors) {
      setErrors(e.data.errors);
    }
    console.error('Submission error:', e);
  } finally {
    loading.value = false;
  }
});
</script>

<template>
  <form
    :class="['form-consultation', view ? `form-consultation--${view}` : '']"
    @submit.prevent="onSubmit"
  >
    <div
      class="form-consultation__item"
      :class="{ 'form-consultation__item--error': !!phone.errorMessage.value }"
    >
      <FloatLabel
        variant="on"
        :class="[
          view === 'support'
            ? 'floatlabel-onwhite'
            : 'floatlabel-ontransparent',
        ]"
      >
        <InputMask
          id="consultationPhone"
          v-model="phone.value.value"
          class="form-consultation__item-field"
          :class="[view === 'support' ? '' : 'input-dark']"
          mask="+380 99 999 9999"
          placeholder="+380"
          fluid
          :invalid="!!phone.errorMessage.value"
        />
        <label
          for="consultationPhone"
          class="form-consultation__item-label"
          :class="{ 'error-100': !!phone.errorMessage.value }"
        >
          {{ $t('consultation.phone') }}*
        </label>
      </FloatLabel>
    </div>
    <Button
      :label="$t('consultation.send_button')"
      type="submit"
      rounded
      class="form-consultation__button"
      :class="[view === 'support' ? 'button-black' : 'button-white']"
      :loading="loading"
      :disabled="!isRequiredFilled"
      severity="secondary"
    />
  </form>
</template>

<style scoped lang="scss">
.form-consultation {
  display: flex;
  flex-direction: column;
  gap: 16px;
  @include media-breakpoint-up(xl) {
    @include responsive-spacing(gap, 16);
  }
  &__item {
    position: relative;
    @include media-breakpoint-up(md) {
    }
  }
  &__button {
    max-width: fit-content;
  }
}
</style>
